import React, { useEffect, useState } from "react";
import {
  Select,
  Space,
  Typography,
  Form,
  message,
  Result,
  Button,
  Modal,
} from "antd";
import {
  PlusOutlined,
  LockOutlined,
  MailOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import Icon from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchAllWorkspacesAction } from "../slices/workspaceSlice";
import { canOverrideInviteBlock, isDashmoteStaffEmail } from "../slices/authSlice";
import { inviteUsersToWorkspaces } from "../api/internal_tool_endpoints";
import { adminResendInvitation } from "../api/invite_link_endpoints";
import { ExportSvg, SearchSvg } from "../assets/icons";
import {
  UserManagementGlobalStyles,
  UserManagementContainer,
  PageTitle,
  StyledSearchInput,
  UserManagementTable,
  ModalStyles,
} from "../styles/UserManagement.styled";
import { useUserManagement } from "../hooks/UserManagementPage/useUserManagement.jsx";
import { useUserUtils } from "../hooks/UserManagementPage/useUserUtils.jsx";
import { useUserTableColumns } from "../hooks/UserManagementPage/useUserTableColumns.jsx";
import { CustomPagination } from "../components/UserManagementPage/CustomPagination.jsx";
import { DeleteConfirmationModal } from "../components/UserManagementPage/DeleteConfirmationModal.jsx";
import { BulkDeleteConfirmationModal } from "../components/UserManagementPage/BulkDeleteConfirmationModal.jsx";
import { SuccessModal } from "../components/UserManagementPage/SuccessModal.jsx";
import { MarketView } from "../components/UserManagementPage/MarketView.jsx";
import { UserListHeader } from "../components/UserManagementPage/UserListHeader.jsx";
import { DeactivatedEntry } from "../components/UserManagementPage/DeactivatedEntry.jsx";
import ApiKeysView from "../components/UserManagementPage/ApiKeysView.jsx";
import { SearchMissHint } from "../components/UserManagementPage/SearchMissHint.jsx";
import InviteUsersModal from "../components/InternalConfigurationTable/InviteUsersModal.jsx";
import UserCreatedResultModal from "../components/InternalConfigurationTable/UserCreatedResultModal.jsx";
import { ResetPasswordChoiceModal } from "../components/UserManagementPage/ResetPasswordChoiceModal.jsx";
import { DeliveryChoiceModal } from "../components/UserManagementPage/DeliveryChoiceModal.jsx";
import { ResetPasswordResultModal } from "../components/UserManagementPage/ResetPasswordResultModal.jsx";
import BlockedInviteDialog from "../components/UserManagementPage/BlockedInviteDialog.jsx";
import { workspaceResetPassword } from "../api/userManagementApi";
import { canResetPasswordRow } from "../utils/roleUtils";

const { Title } = Typography;
const { Option } = Select;

const ExportIcon = (props) => <Icon component={ExportSvg} {...props} />;
const SearchIcon = (props) => <Icon component={SearchSvg} {...props} />;

const UserManagementPage = () => {
  // Initialize hooks
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userManagement = useUserManagement();
  const { getRoleTagOption, commonButtonStyles } = useUserUtils();

  // Get user data and current workspace from Redux
  const { userData, selectedWorkspace } = useSelector((state) => state.auth);

  // Invite modal state and form
  const [inviteForm] = Form.useForm();
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  const [createdUsers, setCreatedUsers] = useState([]);
  // Set when an invite is refused for a disallowed email domain:
  // { data, detail, canOverride }. Drives the block/override dialog.
  const [blockedInvite, setBlockedInvite] = useState(null);
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);
  const [isLinkResultModalOpen, setIsLinkResultModalOpen] = useState(false);
  const [generatedLinks, setGeneratedLinks] = useState([]);

  // Reset-password modal state. resetResult holds the one-time temp password
  // for the copy flow — cleared the moment the result modal closes.
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [resetTargetEmail, setResetTargetEmail] = useState(null);
  const [isResetting, setIsResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [isResetResultOpen, setIsResetResultOpen] = useState(false);
  // Send New Invitation — target email (drives the delivery chooser) + spinner.
  const [resendTarget, setResendTarget] = useState(null);
  const [isResending, setIsResending] = useState(false);
  // Create-API-Key modal open state. Lifted here so its trigger button can sit
  // in the shared controls row (next to the view toggle) instead of a second
  // toolbar row inside ApiKeysView; the modal itself still lives in that view.
  const [apiKeyCreateOpen, setApiKeyCreateOpen] = useState(false);

  // Check if user has access to User Management page
  const hasAccess = () => {
    if (!userData || !selectedWorkspace) {
      return false;
    }

    const workspaceKey = selectedWorkspace?.key || selectedWorkspace;

    const userRole = userData?.id_client?.[workspaceKey]?.role;

    // Normalize the backend role's casing/spacing before the allowlist check.
    const normalizedRole = userRole?.toLowerCase().replace(/\s+/g, '_');

    const allowedRoles = ['super_admin', 'admin'];
    const hasAccess = allowedRoles.includes(normalizedRole);

    return hasAccess;
  };

  // API-key management is limited to the roles the backend lets manage keys:
  // Dashmote staff, or a super_admin of the selected workspace. A plain admin
  // reaches the page but would 403 on the keys endpoints, so no tab for them.
  const canManageApiKeys = () => {
    if (!userData) return false;
    if (isDashmoteStaffEmail(userData.email)) return true;
    const workspaceKey = selectedWorkspace?.key || selectedWorkspace;
    const role = userData?.id_client?.[workspaceKey]?.role;
    return role?.toLowerCase().replace(/\s+/g, "_") === "super_admin";
  };

  // Fetch workspace data for the currently selected workspace on mount
  useEffect(() => {
    const idClient = selectedWorkspace?.key || selectedWorkspace;
    if (idClient) {
      dispatch(fetchAllWorkspacesAction(idClient));
    }
  }, [dispatch, selectedWorkspace]);

  // Destructure all the values and functions from the main hook
  const {
    // Data
    users,
    activeUsers,
    loading,
    hasFetchedData,
    assignableRoles,
    contextHolder: dataContextHolder,
    regionMarketData, // Dynamic region-market data from Redux

    // Filtering
    searchTerm,
    setSearchTerm,
    viewMode,
    setViewMode,
    currentPage,
    pageSize,
    filteredUsers,
    paginatedUsers,
    uniqueUserCount,
    activeCount,
    deactivatedCount,
    deactivatedMatchCount,
    handlePaginationChange,
    handlePageSizeChange,

    // Active/Deactivated view
    statusFilter,
    setStatusFilter,

    // Selection
    selectedRows,
    setSelectedRows,
    actualSelectedCount,
    rowSelection,

    // Editing
    editingRows,
    openDropdowns,
    stagingData,
    startEditUser,
    stopEditUser,
    handleEditUser,
    handleSaveUser,
    handleStageChange,
    handleMarketDropdownOpenChange,
    handleRoleDropdownOpenChange,
    handleProductDropdownOpenChange,
    handleMarketSelection,
    handleRegionExpansion,
    handleRegionSelection,
    isRegionFullySelected,
    isMarketSelected,
    handleAddModule,
    isSaving,

    // Delete Modal
    deleteModalVisible,
    userToDelete,
    deleteType,
    isDeleting,
    handleDeleteUser,
    confirmDeleteUser,
    cancelDeleteUser,

    // Deactivated-view actions
    handleReactivateUser,
    handlePermanentDelete,
    isReactivating,

    // Bulk Delete Modal
    bulkDeleteModalVisible,
    handleBulkDelete,
    confirmBulkDelete,
    cancelBulkDelete,

    // Success Modal
    successModalVisible,
    deletedUser,
    bulkDeletedCount,
    bulkDeletedMarkets,
    deletedUsers,
    isRestoringUsers,
    handleRestoreUsers,
    handleSuccessModalClose,
    handleExport: _handleExport, // Reserved for future export functionality

    // Context holders
    contextHolder: actionsContextHolder,

    // New functions for hierarchical data
    calculateRowSpans,
  } = userManagement;

  // Resend Invitation — surfaced only on `pending_activation` rows. Opens the
  // shared delivery chooser: email the fresh link, or copy it to share manually.
  const handleResendInvitation = (record) => {
    if (!record?.email) return;
    setResendTarget(record.email);
  };

  const handleConfirmResend = async (email, delivery) => {
    setIsResending(true);
    const hide = message.loading({
      key: "resend-invite",
      content:
        delivery === "copy"
          ? "Generating invitation link..."
          : "Sending new invitation link...",
      duration: 0,
    });
    try {
      // Authenticated admin endpoint so the audit log records actor =
      // workspace_admin / dashmote_staff. delivery="copy" returns the link to
      // share manually; "email" mails it. Customer self-service keeps calling
      // the public requestNewInvitation from /invitation-expired.
      const result = await adminResendInvitation({ email, delivery });
      hide();
      setResendTarget(null);
      if (delivery === "copy") {
        // Reuse the generate-link result modal to present the copyable link.
        setGeneratedLinks([
          { email, invitation_link: result?.invitation_link },
        ]);
        setIsLinkResultModalOpen(true);
      } else {
        message.success({
          key: "resend-invite",
          content: `New invitation link sent to ${email}.`,
          duration: 3,
        });
      }
    } catch (error) {
      hide();
      if (error?.status === 429) {
        message.error({
          key: "resend-invite",
          content:
            "Too many requests for this email. Please wait a few minutes and try again.",
          duration: 4,
        });
      } else {
        message.error({
          key: "resend-invite",
          content:
            error?.message ||
            "Failed to send a new invitation link. Please try again.",
          duration: 4,
        });
      }
    } finally {
      setIsResending(false);
    }
  };

  // Reset password handlers. The row supplies the target email; the choice
  // modal picks the delivery (copy / email); the backend enforces the real
  // tenant + hierarchy authorization and its `detail` is surfaced verbatim.
  const callerWorkspaceRole =
    userData?.id_client?.[selectedWorkspace?.key || selectedWorkspace]?.role;

  const handleResetPassword = (record) => {
    if (!record?.email) return;
    setResetTargetEmail(record.email);
    setIsResetModalOpen(true);
  };

  const handleResetModalClose = () => {
    setIsResetModalOpen(false);
    setResetTargetEmail(null);
  };

  const handleConfirmResetPassword = async (email, delivery) => {
    setIsResetting(true);
    try {
      const result = await workspaceResetPassword(email, delivery);
      setResetResult({ email, delivery, ...result });
      setIsResetModalOpen(false);
      setResetTargetEmail(null);
      setIsResetResultOpen(true);
    } catch (error) {
      message.error(error.message || "Failed to reset the password.");
    } finally {
      setIsResetting(false);
    }
  };

  // Cosmetic per-row gate: a plain admin only sees Reset on normal-user rows.
  const canResetPassword = (record) =>
    canResetPasswordRow(callerWorkspaceRole, record?.roleName || record?.role);

  // Get table columns
  const { columns } = useUserTableColumns({
    editingRows,
    openDropdowns,
    stagingData,
    getRoleTagOption,
    commonButtonStyles,
    handleMarketDropdownOpenChange,
    handleRoleDropdownOpenChange,
    handleProductDropdownOpenChange,
    handleSaveUser,
    handleStageChange,
    startEditUser,
    stopEditUser,
    handleDeleteUser,
    handleResendInvitation,
    handleResetPassword,
    canResetPassword,
    handleReactivateUser,
    handlePermanentDelete,
    isReactivating,
    handleAddModule,
    isSaving,
    users: paginatedUsers,
    allUsers: filteredUsers,
    calculateRowSpans: calculateRowSpans,
    regionMarketData,
    assignableRoles,
    currentUserEmail: userData?.email,
  });

  // Invite handlers - matching Internal Configuration flow
  const handleInviteClick = () => {
    setIsInviteModalOpen(true);
  };

  const handleInviteModalClose = () => {
    setIsInviteModalOpen(false);
    inviteForm.resetFields();
  };

  const submitInvites = async (data, domainOverrideReason) => {
    const hide = message.loading("Sending invitations...", 0);
    try {
      const results = await inviteUsersToWorkspaces({
        emails: data.emails,
        workspaceAssignments: data.workspaceAssignments,
        domainOverrideReason,
      });
      hide();
      setIsInviteModalOpen(false);
      setBlockedInvite(null);
      const usersData = results.map((r) => r.result);
      setCreatedUsers(usersData);
      setIsResultModalOpen(true);
      message.success({ content: `${usersData.length} user${usersData.length > 1 ? "s" : ""} invited successfully!`, key: "invite-success" });
    } catch (error) {
      hide();
      // Domain-allowlist refusal → the block/override dialog, not a generic
      // toast. Branch on the machine code, never the (translatable) prose.
      if (error.code === "invite_domain_not_allowed") {
        setBlockedInvite({
          data,
          detail: error.message,
          canOverride: canOverrideInviteBlock(userData),
        });
        return;
      }
      console.error("Failed to invite users:", error);
      message.error({ content: error.message || "Failed to invite users. Please try again.", key: "invite-error" });
    }
  };

  const handleUsersInvited = (data) => submitInvites(data);

  const handleOverrideInvite = async (reason) => {
    if (!blockedInvite) return;
    setOverrideSubmitting(true);
    await submitInvites(blockedInvite.data, reason);
    setOverrideSubmitting(false);
  };

  const handleGenerateLink = async (data) => {
    const hide = message.loading("Generating invite links...", 0);
    try {
      const results = await inviteUsersToWorkspaces({
        emails: data.emails,
        workspaceAssignments: data.workspaceAssignments,
        sendEmail: false,
      });
      hide();
      setIsInviteModalOpen(false);
      const usersData = results.map((r) => r.result);
      setGeneratedLinks(usersData);
      setIsLinkResultModalOpen(true);
    } catch (error) {
      console.error("Failed to generate invite links:", error);
      hide();
      message.error({ content: error.message || "Failed to generate invite links. Please try again.", key: "link-error" });
    }
  };

  const handleGenerateGroupLink = async () => {
    // TODO: call POST /invite-links once the backend endpoint is ready.
    // Expected payload: { domainRestriction, workspaceAssignments }
  };

  // If user doesn't have access, show access denied message
  if (!hasAccess()) {
    return (
      <>
        <UserManagementGlobalStyles />
        <UserManagementContainer>
          <div className="flex items-center justify-center min-h-[60vh]">
            <Result
              icon={<LockOutlined style={{ fontSize: '72px', color: '#ff4d4f' }} />}
              status="403"
              title="Access Denied"
              subTitle="You need Super Admin, Admin, or Dashmote Admin role in this workspace to access User Management."
              extra={
                <Button type="primary" onClick={() => navigate('/')}>
                  Back to Home
                </Button>
              }
            />
          </div>
        </UserManagementContainer>
      </>
    );
  }

  return (
    <>
      <UserManagementGlobalStyles />
      <ModalStyles />
      <UserManagementContainer>
        {dataContextHolder}
        {actionsContextHolder}

        {/* Header */}
        <PageTitle>User Management</PageTitle>

        {/* Combined Controls and Table Container */}
        <div className="bg-white rounded-lg py-6">
          {/* Controls Section */}
          <div className="flex justify-between gap-6 items-center flex-1">
            <div className="h-[3.25rem] border border-[rgba(0,0,0,0.15)] p-[0.19rem] inline-flex gap-1 rounded-full min-w-fit">
              <button
                style={{
                  color:
                    viewMode === "User list"
                      ? "#0e49b5"
                      : "rgba(0, 0, 0, 0.45)",
                }}
                className={`h-[2.75rem] px-6 flex items-center text-base rounded-full box-border font-medium transition-all duration-100 ease-in-out whitespace-nowrap ${
                  viewMode === "User list"
                    ? "bg-[#f5f8fc] border-[1.8px] border-[#0e49b5] text-[#0e49b5]"
                    : "border-[1.8px] border-[#fff]"
                } hover:bg-gray-50 cursor-pointer`}
                onClick={() => setViewMode("User list")}
              >
                User List
              </button>
              <button
                style={{
                  color:
                    viewMode === "Market view"
                      ? "#0e49b5"
                      : "rgba(0, 0, 0, 0.45)",
                }}
                className={`h-[2.75rem] px-6 flex items-center text-base rounded-full box-border font-medium transition-all duration-100 ease-in-out whitespace-nowrap ${
                  viewMode === "Market view"
                    ? "bg-[#f5f8fc] border-[1.8px] border-[#0e49b5] text-[#0e49b5]"
                    : "border-[1.8px] border-[#fff]"
                } hover:bg-gray-50 cursor-pointer`}
                onClick={() => setViewMode("Market view")}
              >
                Market View
              </button>
              {/* API Keys: a workspace-scoped tab for staff and workspace
                  super_admins (the roles the backend lets manage keys). Clients
                  manage their own workspace's keys here; the backend returns
                  only customer keys and hides internal/system ones. */}
              {canManageApiKeys() && (
                <button
                  style={{
                    color:
                      viewMode === "API Keys"
                        ? "#0e49b5"
                        : "rgba(0, 0, 0, 0.45)",
                  }}
                  className={`h-[2.75rem] px-6 flex items-center text-base rounded-full box-border font-medium transition-all duration-100 ease-in-out whitespace-nowrap ${
                    viewMode === "API Keys"
                      ? "bg-[#f5f8fc] border-[1.8px] border-[#0e49b5] text-[#0e49b5]"
                      : "border-[1.8px] border-[#fff]"
                  } hover:bg-gray-50 cursor-pointer`}
                  onClick={() => setViewMode("API Keys")}
                >
                  API Keys
                </button>
              )}
            </div>
            {viewMode !== "API Keys" && (
              <>
                <div className="w-full flex-1">
                  <StyledSearchInput
                    placeholder="Search by name or email"
                    prefix={<SearchIcon className="ml-1 mr-1" />}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleInviteClick}
                  className="h-[3.25rem] bg-[#0e49b5] text-white p-[8px_30px_8px_22px] rounded-full flex items-center gap-2 font-medium transition-colors text-base"
                >
                  <PlusOutlined className="mr-1 text-xl" />
                  Invite User
                </button>
              </>
            )}
            {viewMode === "API Keys" && canManageApiKeys() && (
              <button
                type="button"
                onClick={() => setApiKeyCreateOpen(true)}
                className="h-[3.25rem] bg-[#0e49b5] text-white p-[8px_30px_8px_22px] rounded-full flex items-center gap-2 font-medium transition-colors text-base"
              >
                <PlusOutlined className="mr-1 text-xl" />
                Create API Key
              </button>
            )}
          </div>
          {/* Table Section with 1.5rem gap */}
          <div className="mt-6">
            {viewMode === "User list" && (
              <>
                {/* Active/Deactivated: the view heading names the count ("N
                    active users"), and a single count-bearing Deactivated chip /
                    Back link is the entry point. It filters the User list rather
                    than adding a second toggle beside the primary view tabs. */}
                <UserListHeader
                  actualSelectedCount={actualSelectedCount}
                  filteredUsersCount={filteredUsers.length}
                  onBulkDelete={handleBulkDelete}
                  heading={
                    statusFilter === "deactivated"
                      ? `${deactivatedCount} deactivated ${deactivatedCount === 1 ? "user" : "users"}`
                      : `${activeCount} active ${activeCount === 1 ? "user" : "users"}`
                  }
                  // The entry point sits LEFT on the deactivated view (back
                  // navigation) and RIGHT on the active view (the count chip).
                  leadSlot={
                    statusFilter === "deactivated" ? (
                      <DeactivatedEntry
                        statusFilter={statusFilter}
                        deactivatedCount={deactivatedCount}
                        onChange={setStatusFilter}
                      />
                    ) : null
                  }
                  rightSlot={
                    statusFilter === "deactivated" ? null : (
                      <DeactivatedEntry
                        statusFilter={statusFilter}
                        deactivatedCount={deactivatedCount}
                        onChange={setStatusFilter}
                      />
                    )
                  }
                />
                <div className="relative">
                  <UserManagementTable
                    rowSelection={rowSelection}
                    columns={columns}
                    dataSource={paginatedUsers}
                    loading={loading}
                    pagination={false}
                    locale={{
                      emptyText:
                        loading || !hasFetchedData ? null : statusFilter ===
                            "active" && searchTerm ? (
                          <SearchMissHint
                            searchTerm={searchTerm}
                            deactivatedMatchCount={deactivatedMatchCount}
                            onViewDeactivated={() => setStatusFilter("deactivated")}
                          />
                        ) : statusFilter === "deactivated" ? (
                          "No deactivated users"
                        ) : (
                          "No active users"
                        ),
                    }}
                  />
                  <CustomPagination
                    total={uniqueUserCount}
                    current={currentPage}
                    pageSize={pageSize}
                    onChange={handlePaginationChange}
                    onShowSizeChange={handlePageSizeChange}
                  />
                </div>
              </>
            )}
            {viewMode === "Market view" && (
              <MarketView
                users={activeUsers}
                searchTerm={searchTerm}
                editingRows={editingRows}
                openDropdowns={openDropdowns}
                stagingData={stagingData}
                getRoleTagOption={getRoleTagOption}
                commonButtonStyles={commonButtonStyles}
                handleMarketDropdownOpenChange={handleMarketDropdownOpenChange}
                handleRegionExpansion={handleRegionExpansion}
                handleRegionSelection={handleRegionSelection}
                handleMarketSelection={handleMarketSelection}
                isRegionFullySelected={isRegionFullySelected}
                isMarketSelected={isMarketSelected}
                handleRoleDropdownOpenChange={handleRoleDropdownOpenChange}
                handleProductDropdownOpenChange={
                  handleProductDropdownOpenChange
                }
                handleStageChange={handleStageChange}
                handleSaveUser={handleSaveUser}
                startEditUser={startEditUser}
                stopEditUser={stopEditUser}
                handleEditUser={handleEditUser}
                handleDeleteUser={handleDeleteUser}
                handleResendInvitation={handleResendInvitation}
                handleAddModule={handleAddModule}
                currentUserEmail={userData?.email}
                isSaving={isSaving}
                onBulkDelete={handleBulkDelete}
                setSelectedRows={setSelectedRows}
              />
            )}
            {viewMode === "API Keys" && (
              <ApiKeysView
                createModalOpen={apiKeyCreateOpen}
                onCreateOpenChange={setApiKeyCreateOpen}
              />
            )}
          </div>
        </div>

        {/* Invite Users Modal - Same as Internal Configuration */}
        <InviteUsersModal
          open={isInviteModalOpen}
          onCancel={handleInviteModalClose}
          onOk={handleUsersInvited}
          onGenerateLink={handleGenerateLink}
          onGenerateGroupLink={handleGenerateGroupLink}
          form={inviteForm}
          assignableRoles={assignableRoles}
        />

        <BlockedInviteDialog
          open={!!blockedInvite}
          detail={blockedInvite?.detail}
          canOverride={blockedInvite?.canOverride}
          submitting={overrideSubmitting}
          onCancel={() => setBlockedInvite(null)}
          onOverride={handleOverrideInvite}
        />

        {/* Result Modal - Email invitation sent */}
        <UserCreatedResultModal
          open={isResultModalOpen}
          onClose={() => setIsResultModalOpen(false)}
          users={createdUsers}
        />

        {/* Result Modal - Invite links generated */}
        <UserCreatedResultModal
          open={isLinkResultModalOpen}
          onClose={() => setIsLinkResultModalOpen(false)}
          users={generatedLinks}
          mode="link"
        />

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          open={deleteModalVisible}
          onCancel={cancelDeleteUser}
          onConfirm={confirmDeleteUser}
          userToDelete={userToDelete}
          deleteType={deleteType}
          isDeleting={isDeleting}
        />

        {/* Bulk Delete Confirmation Modal */}
        <BulkDeleteConfirmationModal
          open={bulkDeleteModalVisible}
          onCancel={cancelBulkDelete}
          onConfirm={confirmBulkDelete}
          selectedRows={selectedRows}
          users={users}
        />

        {/* Success Modal */}
        <SuccessModal
          open={successModalVisible}
          onClose={handleSuccessModalClose}
          onRestore={handleRestoreUsers}
          isRestoringUsers={isRestoringUsers}
          deletedUser={deletedUser}
          deleteType={deleteType}
          bulkDeletedCount={bulkDeletedCount}
          bulkDeletedMarkets={bulkDeletedMarkets}
          deletedUsers={deletedUsers}
        />

        {/* Reset Password - delivery choice (per-row entry, email is fixed) */}
        <ResetPasswordChoiceModal
          open={isResetModalOpen}
          isResetting={isResetting}
          targetEmail={resetTargetEmail}
          onConfirm={handleConfirmResetPassword}
          onCancel={handleResetModalClose}
        />

        {/* Send New Invitation — email the fresh link or copy it to share. */}
        <DeliveryChoiceModal
          open={!!resendTarget}
          isSubmitting={isResending}
          targetEmail={resendTarget}
          onConfirm={handleConfirmResend}
          onCancel={() => setResendTarget(null)}
          title="Send New Invitation"
          description={
            <>
              A fresh invitation link will be generated and the previous link
              will stop working. Choose <strong>Email</strong> to send it to the
              user, or <strong>Copy link</strong> to share it yourself.
            </>
          }
          options={[
            {
              key: "copy",
              label: "Copy invite link",
              icon: <LinkOutlined />,
            },
            {
              key: "email",
              label: "Email invitation",
              icon: <MailOutlined />,
              primary: true,
            },
          ]}
        />

        {/* Reset Password - result (plaintext dropped when closed) */}
        <ResetPasswordResultModal
          open={isResetResultOpen}
          onClose={() => {
            setIsResetResultOpen(false);
            setResetResult(null);
          }}
          result={resetResult}
        />
      </UserManagementContainer>
    </>
  );
};

export default UserManagementPage;
